<template>
  <Wrapper class="q-px-lg q-py-xl">
    <h5 class="q-mb-xl">FAQ</h5>

    <div class="container-fluid bg-gray q-mb-md overflow-auto">
      <div class="row items-center q-pa-xs q-col-gutter-sm">
        <div class="col-12 col-sm-3 col-xl-2">
          <q-btn outline class="full-width" color="primary" icon="add" label="new faq" to="/system/faq/new" />
        </div>

        <div class="col-12 col-sm-3 col-xl-2 items-center">
          <q-btn
            flat
            class="full-width"
            color="primary"
            icon="add"
            label="add topic"
            @click="isTopicDialogOpen = true"
          />
        </div>

        <div class="col-12 col-sm-3 col-xl-3">
          <q-input v-model="search" label="Search" @keypress.enter="debounceFetchData" />
        </div>

        <div class="col-12 col-sm-3 col-xl-2">
          <q-btn class="full-width" color="primary" icon="search" label="Search" @click="debounceFetchData" />
        </div>
      </div>
    </div>

    <div class="flex justify-end q-mb-sm">
      <q-btn outline color="primary" icon="refresh" label="Refresh" @click="debounceFetchData" />
    </div>

    <DataTable ref="table" :columns="columns">
      <template v-if="!faqs.length">
        <template v-if="isLoading">
          <tr v-for="n in 10" :key="n">
            <td v-for="m in columns.length" :key="m">
              <q-skeleton type="text" />
            </td>
          </tr>
        </template>

        <tr v-else>
          <td class="text-center" :colspan="columns.length">
            <p>You do not have any help articles.</p>
            <q-btn color="primary" label="Create new help article" to="faq/new" />
          </td>
        </tr>
      </template>

      <tr v-for="faq in faqs" v-else :key="faq.id">
        <td>
          <span v-if="faq.topic">{{ faq.topic.name }}</span>
          <span v-else>Unknown</span>
        </td>

        <td>{{ faq.title | truncate(100) }}</td>
        <td>{{ faq.content | stripHtmlTags | truncate(25) }}</td>
        <td>
          <span>{{ faq.likedUsers.length }}</span>
          <q-icon name="thumb_up" size="1.5em" color="positive" style="margin-left: 5px" />
          <q-icon name="thumb_down" size="1.5em" color="negative" style="margin-left: 7px; margin-right: 5px" />
          <span>{{ faq.dislikedUsers.length }}</span>
        </td>
        <td>{{ faq.createdAt | formatDate }}</td>
        <td class="q-gutter-x-sm">
          <router-link :to="`faq/edit/${faq.slug}`"> Change </router-link>
          <span class="text-negative cursor-pointer" @click="openDeleteDialog(faq)"> Delete </span>
        </td>
      </tr>
    </DataTable>

    <Pagination v-if="totalPages > 1" :page.sync="currentPage" :total="totalPages" />

    <q-dialog v-model="isDeleteDialogOpen" :persistent="isDeleteDialogLoading">
      <q-card>
        <q-card-section>
          <h6>Delete FAQ</h6>
        </q-card-section>

        <q-card-section>
          <p>Are you sure you want to delete this FAQ?</p>
          <p v-if="selectedItem" class="text-center text-weight-bold">
            {{ selectedItem.title }}
          </p>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat color="muted" label="Cancel" :disable="isDeleteDialogLoading" />
          <q-btn flat color="negative" label="Delete" :loading="isDeleteDialogLoading" @click="deleteFaq" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <TopicDialog :is-open.sync="isTopicDialogOpen" />
  </Wrapper>
</template>

<script>
import DataTable from '@/components/DataTableNew.vue';
import debounce from 'lodash/debounce';
import Wrapper from '@/components/Wrapper.vue';
import { getErrorMessage } from '@/utils';
import Pagination from '@/components/Pagination.vue';
import TopicDialog from './FAQ/TopicDialog.vue';

export default {
  components: {
    Wrapper,
    DataTable,
    Pagination,
    TopicDialog,
  },

  filters: {
    stripHtmlTags(val) {
      return val.replace(/<[^>]*>?/gm, '');
    },
  },

  data() {
    return {
      isLoading: false,
      faqs: [],
      currentPage: 1,
      totalPages: 0,
      search: '',
      isDeleteDialogOpen: false,
      isDeleteDialogLoading: false,
      selectedItem: null,
      isTopicDialogOpen: false,
      columns: ['Category', 'Title', 'Detail', 'Rated', 'Created Time', 'Actions'],
    };
  },

  watch: {
    currentPage() {
      this.debounceFetchData();
      window.scrollTo({ top: this.$refs.table.$el.offsetTop, behavior: 'smooth' });
    },
  },

  created() {
    this.debounceFetchData = debounce(this.fetchData, 500);
    this.fetchData();
  },

  methods: {
    async fetchData() {
      this.isLoading = true;

      try {
        const params = new URLSearchParams();
        params.append('q', this.search);
        params.append('page', this.currentPage);

        const {
          data: { data, pagination },
        } = await this.$axios.get('/admin/faq', { params });

        this.faqs = data;
        this.currentPage = pagination.page;
        this.totalPages = pagination.pages;
      } catch (error) {
        this.$q.notify({
          message: getErrorMessage(error),
          type: 'negative',
          actions: [{ handler: this.fetchData, label: 'Try again', color: 'yellow' }],
        });
      } finally {
        this.isLoading = false;
      }
    },
    debounceFetchData() {},
    openDeleteDialog(faq) {
      this.isDeleteDialogOpen = true;
      this.selectedItem = faq;
    },
    async deleteFaq() {
      this.isDeleteDialogLoading = true;

      try {
        await this.$axios.delete(`/admin/faq/${this.deleteFaqModal.faq.slug}`);

        this.$q.notify({ message: 'Help article successfully delete', type: 'positive' });
        await this.fetchData();

        this.isDeleteDialogOpen = false;
        this.selectedItem = null;
      } catch (error) {
        this.$q.notify({ message: getErrorMessage(error), type: 'negative' });
      } finally {
        this.isDeleteDialogLoading = false;
      }
    },
  },
};
</script>
